import React from 'react';
import {Page, Layout, Card, TextStyle, Button} from '@shopify/polaris';
import {useUserRole} from '../../contexts/userRoleContext';
import {useHistory} from 'react-router-dom';

/**
 * Higher-Order Component for protecting routes based on roles
 * @param {Object} options - Protection options
 * @param {string[]} options.requiredRoles - Array of roles required to access
 * @param {string[]} options.requiredApps - Array of app IDs required to access
 * @param {boolean} options.requireAdmin - Whether admin access is required
 * @param {boolean} options.requireAnyRole - Whether any of the required roles is sufficient (default: false - requires all)
 * @param {React.Component} options.fallbackComponent - Custom component to show when access is denied
 */
export function withRoleProtection(options = {}) {
  const {
    requiredRoles = [],
    requiredApps = [],
    requireAdmin = false,
    requireAnyRole = false,
    fallbackComponent: FallbackComponent = null
  } = options;

  return function ProtectedComponent(WrappedComponent) {
    return function WithRoleProtection(props) {
      const {
        loading,
        error,
        isAuthenticated,
        isAdmin,
        hasRole,
        hasAnyRole,
        hasAllRoles,
        canAccessApp,
        user
      } = useUserRole();

      const history = useHistory();

      // Show loading state
      if (loading) {
        return (
          <Page>
            <Layout>
              <Layout.Section>
                <Card sectioned>
                  <p>Loading...</p>
                </Card>
              </Layout.Section>
            </Layout>
          </Page>
        );
      }

      // Show error state
      if (error) {
        return (
          <Page>
            <Layout>
              <Layout.Section>
                <Card sectioned>
                  <TextStyle variation="negative">
                    Error loading user information: {error}
                  </TextStyle>
                </Card>
              </Layout.Section>
            </Layout>
          </Page>
        );
      }

      // Check authentication
      if (!isAuthenticated) {
        return (
          <Page>
            <Layout>
              <Layout.Section>
                <Card sectioned>
                  <TextStyle variation="subdued">Please log in to access this page.</TextStyle>
                </Card>
              </Layout.Section>
            </Layout>
          </Page>
        );
      }

      // Check admin requirement
      if (requireAdmin && !isAdmin) {
        if (FallbackComponent) {
          return <FallbackComponent {...props} />;
        }

        return (
          <Page>
            <Layout>
              <Layout.Section>
                <Card sectioned>
                  <TextStyle variation="negative">
                    <h2>Access Denied</h2>
                    <p>You need administrator privileges to access this page.</p>
                    <p>Current user: {user?.email}</p>
                    <Button onClick={() => history.goBack()}>Go Back</Button>
                  </TextStyle>
                </Card>
              </Layout.Section>
            </Layout>
          </Page>
        );
      }

      // Check role requirements
      if (requiredRoles.length > 0) {
        const hasRequiredRoles = requireAnyRole
          ? hasAnyRole(requiredRoles)
          : hasAllRoles(requiredRoles);

        if (!hasRequiredRoles) {
          if (FallbackComponent) {
            return <FallbackComponent {...props} />;
          }

          return (
            <Page>
              <Layout>
                <Layout.Section>
                  <Card sectioned>
                    <TextStyle variation="negative">
                      <h2>Insufficient Permissions</h2>
                      <p>
                        You need the following {requireAnyRole ? '(any of)' : '(all of)'} roles to
                        access this page:
                      </p>
                      <ul>
                        {requiredRoles.map(role => (
                          <li key={role}>
                            {role} {hasRole(role) ? '✅' : '❌'}
                          </li>
                        ))}
                      </ul>
                      <Button onClick={() => history.goBack()}>Go Back</Button>
                    </TextStyle>
                  </Card>
                </Layout.Section>
              </Layout>
            </Page>
          );
        }
      }

      // Check app access requirements
      if (requiredApps.length > 0) {
        const hasAppAccess = requiredApps.some(appId => canAccessApp(appId));

        if (!hasAppAccess) {
          if (FallbackComponent) {
            return <FallbackComponent {...props} />;
          }

          return (
            <Page>
              <Layout>
                <Layout.Section>
                  <Card sectioned>
                    <TextStyle variation="negative">
                      <h2>App Access Required</h2>
                      <p>You need access to one of the following apps:</p>
                      <ul>
                        {requiredApps.map(appId => (
                          <li key={appId}>
                            {appId} {canAccessApp(appId) ? '✅' : '❌'}
                          </li>
                        ))}
                      </ul>
                      <Button onClick={() => history.goBack()}>Go Back</Button>
                    </TextStyle>
                  </Card>
                </Layout.Section>
              </Layout>
            </Page>
          );
        }
      }

      // All checks passed, render the protected component
      return <WrappedComponent {...props} />;
    };
  };
}

/**
 * Hook for conditional rendering based on roles
 */
export function useRoleAccess(options = {}) {
  const {
    requiredRoles = [],
    requiredApps = [],
    requireAdmin = false,
    requireAnyRole = false
  } = options;

  const {
    loading,
    error,
    isAuthenticated,
    isAdmin,
    hasAnyRole,
    hasAllRoles,
    canAccessApp
  } = useUserRole();

  if (loading || error || !isAuthenticated) {
    return {
      hasAccess: false,
      loading,
      error,
      isAuthenticated
    };
  }

  // Check admin requirement
  if (requireAdmin && !isAdmin) {
    return {hasAccess: false, reason: 'Admin required'};
  }

  // Check role requirements
  if (requiredRoles.length > 0) {
    const hasRequiredRoles = requireAnyRole
      ? hasAnyRole(requiredRoles)
      : hasAllRoles(requiredRoles);

    if (!hasRequiredRoles) {
      return {hasAccess: false, reason: 'Insufficient roles'};
    }
  }

  // Check app access requirements
  if (requiredApps.length > 0) {
    const hasAppAccess = requiredApps.some(appId => canAccessApp(appId));

    if (!hasAppAccess) {
      return {hasAccess: false, reason: 'App access required'};
    }
  }

  return {hasAccess: true};
}
